import { Header } from "semantic-ui-react";
import { TableComponent } from "../../../components/TableComponent/TableComponent";
import moment from "moment";
import { TextChip } from "../../../components/TextChip/TextChip";
import ActionDropdown from "../../../components/ActionDropdown/ActionDropdown";
import { useFetchWithPagination } from "../../../utils/useFetchWithPagination";
import {
  assign_batch_url,
  awb_list_url,
  order_list_url,
} from "../../../api/urls";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useFetch } from "../../../utils/useFetch";
import BackButton from "../../../components/BackButton/BackButton";
import { useState } from "react";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api/api";
import unknownError from "../../../utils/unknownError";
import { useModal } from "../../../components/Modaler";
import { na, na2, namt } from "../../../utils/na";
import { userAddressFormatter } from "../../../utils/addresssFormatter";

const backUrl = "/logistics/delivery_settlement";

export function ViewSettlement() {
  const params = useParams();
  const modaler = useModal();

  const { data, pagination } = useFetchWithPagination(
    awb_list_url +
      `?filter=${JSON.stringify({
        delivery_batch: params.id,
      })}`
  );

  const columns = [
    {
      headerName: "AWB No.",
      field: "awb_no",
    },
    {
      headerName: "Created date",
      field: "created_at",
      renderCell: (params) => moment(params.value).format("DD/MM/YYYY"),
    },
    {
      headerName: "Pick-up address",
      field: "created_at",
      wrap: true,
      renderCell: (params) => params.row?.pickup_address?.address,
    },
    {
      headerName: "Delivery address",
      field: "created_at",
      wrap: true,
      renderCell: (params) => params.row?.shipping_address?.address,
    },
    {
      headerName: "Payment method",
      field: "payment_method",
    },
    {
      headerName: "Earning",
      field: "earning",
    },
    {
      headerName: "Status",
      field: "payment2boy",
      renderCell: (params) => (
        <TextChip
          text={params.value ? "Paid" : "Unpaid"}
          color={params.value ? "#00FF00" : "#FF4500"}
        />
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      renderCell: (params) => (
        <ActionDropdown
          options={(closer) => {
            return (
              <>
                <div
                  className="option"
                  onClick={() => {
                    modaler.opener(
                      <MarkAsPaid
                        d={params.row}
                        refetch={data.refetch}
                        closer={modaler.closer}
                      />,
                      false
                    );
                  }}
                >
                  <div className="purple-title">
                    {params.row.payment2boy ? "Mark as Unpaid" : "Mark as Paid"}
                  </div>
                </div>
                <div
                  className="option"
                  onClick={() => {
                    closer();
                    modaler.opener(
                      <OrderList awbNo={params.row.awb_no} />,
                      true
                    );
                  }}
                >
                  <div className="yellow-title">View orders</div>
                </div>
              </>
            );
          }}
        />
      ),
    },
  ];

  const batchData = useFetch(assign_batch_url + params.id + "/");

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <BackButton href={backUrl} />
        <div className="p-10" />
        <Header>AWB List</Header>
        <div>
          <b>Batch code </b>: {batchData?.data?.batch_code ?? "..."}
        </div>
      </div>
      <div className="p-10" />
      <TableComponent
        loading={data.loading}
        rows={data.data?.results}
        columns={columns}
      />
      {pagination}
    </>
  );
}

function MarkAsPaid({ d, refetch, closer }) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  return (
    <div style={{ padding: "20px" }}>
      <div style={{ fontSize: "22px", fontWeight: "500" }}>Are you sure?</div>
      <div style={{ padding: "15px" }} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <button
          className="btn-red"
          onClick={() => {
            closer();
          }}
        >
          No
        </button>
        <button
          className="btn"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await api.patch(awb_list_url + d.id + "/", {
                payment2boy: !d.payment2boy,
              });
              closer();
              refetch();
              toast.open("Status changed successfully", "success");
            } catch (e) {
              if (e?.response?.status === 400 && e?.response?.data?.data) {
                toast.open(e?.response?.data.data, "error");
              } else {
                unknownError(e);
              }
            }
            setLoading(false);
          }}
        >
          Yes
        </button>
      </div>
    </div>
  );
}

function OrderList({ awbNo }) {
  const columns = [
    {
      headerName: "Order details",
      renderCell: ({ row: d }) => (
        <div
          className="flex-column wrap"
          style={{ textAlign: "left", width: "100%" }}
        >
          <div className="wrap">
            <b>Order id : </b>
            {na2(
              d.order_id,
              <span style={{ color: "#AD00FF" }}>{d.order_id}</span>
            )}
          </div>
          <div>
            <b>Order date : </b>
            {na2(d.created, moment(d.created).format("DD-MM-YYYY"))}
          </div>
          <div>
            <b>Time : </b>
            {na2(d.created, moment(d.created).format("hh:mm A"))}
          </div>
          <div>
            <b>Buyer name : </b>
            {na(d?.customer_name)}
          </div>
          <div>
            <b>Seller name : </b>
            {na(d?.prod_json?.seller_name)}
          </div>
          <div className="wrap">
            <b>Payment mode : </b>
            {na(d.payment_method)}
          </div>
          <div>
            <b>Business type : </b>
            {d.is_company ? "B2B" : "B2C"}
          </div>
        </div>
      ),
    },
    {
      headerName: "Product details",
      renderCell: ({ row: d }) => (
        <div
          className="flex-column"
          style={{ textAlign: "left", width: "100%" }}
        >
          <div className="wrap">
            <b>Product title : </b>
            {na(d?.prod_json?.title)}
          </div>
          <div className="wrap">
            <b>Product description : </b>
            {na(d?.prod_json?.short_description)}
          </div>
          <div>
            <b>Size : </b>
            {na(d.size)}
          </div>
          <div>
            <b>Color : </b>
            {na(d.color)}
          </div>
          <div>
            <b>Quantity : </b>
            {na(d.quantity)}
          </div>
          <div>
            <b>Selling price : </b>
            {namt(d.subtotal_after_tax_amt, data.currency_symbol)}
          </div>
          <div>
            <b>Paid amount : </b>
            {namt(d.total_amount_paid_amt, data.currency_symbol)}
          </div>
          <div>
            <b>Total order value : </b>
            {namt(d.total_amount_paid_amt, data.currency_symbol)}
          </div>
        </div>
      ),
    },
    {
      headerName: "Customer details",
      renderCell: ({ row: d }) => (
        <div
          className="flex-column"
          style={{ textAlign: "left", width: "100%" }}
        >
          {d?.shipping_address?.name && <div>{d.shipping_address.name}</div>}
          <div className="wrap">{userAddressFormatter(d.shipping_address)}</div>
          {d?.shipping_address?.primary_mobile && (
            <div>Mobile No : +{d?.shipping_address?.primary_mobile}</div>
          )}
          <TextChip text={d.action_name} color={"#" + d.icon_color_code} />
          <div className="p-5" />
          <TextChip
            text={d.paid_status}
            color={d.paid_status === "PAID" ? "success" : "error"}
          />
          <div className="p-5" />
          <div
            style={{
              display: "flex",
              gap: "10px",
              width: "100%",
            }}
          >
            <a
              href={d.uploadpayment}
              target="_blank"
              rel="noreferrer"
              style={{ width: "100%", flex: 1 }}
            >
              <button className="btn" style={{ width: "100%" }}>
                View Payment Proof
              </button>
            </a>
            <MarkAsPaidOrder
              id={d.id}
              paidStatus={d.paid_status}
              refetch={data.refetch}
              style={{ flex: 1 }}
            />
          </div>
          {/* <div
            className="active-green no-wrap"
            style={{
              backgroundColor: "#" + d.icon_color_code,
              width: "100%",
              padding: "5px 20px",
              letterSpacing: "0.5px",
            }}
          >
            {na(d.action_name)}
          </div> */}
        </div>
      ),
    },
  ];
  const { data, pagination } = useFetchWithPagination(
    order_list_url + `?filter=${JSON.stringify({ awb_no: awbNo })}`
  );
  return (
    <div
      style={{
        padding: "20px",
        height: "100%",
        maxHeight: "90vh",
        width: "800px",
        maxWidth: "90vw",
        overflow: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Header>Order List</Header>
        <Header>AWB No : {awbNo}</Header>
      </div>
      <div className="p-10" />
      <TableComponent
        loading={data.loading}
        rows={data.data?.results}
        columns={columns}
      />
      {pagination}
    </div>
  );
}

function MarkAsPaidOrder({ id, paidStatus, refetch, style }) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  return (
    <button
      className="btn"
      style={{ width: "100%", ...style }}
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          await api.patch(order_list_url + id + "/Cash_Paid/", {
            paid_status: paidStatus === "PAID" ? "UNPAID" : "PAID",
          });
          refetch();
          toast.open("Status changed successfully", "success");
        } catch (e) {
          if (e?.response?.status === 400 && e?.response?.data?.data) {
            toast.open(e?.response?.data.data, "error");
          } else {
            unknownError(e);
          }
        }
        setLoading(false);
      }}
    >
      {paidStatus === "PAID" ? "Mark as Unpaid" : "Mark as Paid"}
    </button>
  );
}
